import { gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconOperations from '@apollo/icons/default/IconOperations.svg';
import IconProposals from '@apollo/icons/default/IconProposals.svg';
import IconPublic from '@apollo/icons/default/IconPublic.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import {
  Avatar,
  Badge,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Group,
  IconButton,
  Link,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { InitiatingCloudEndpointBadge } from 'src/app/appLayout/components/globalHeaderGraphContent/initiatingCloudEndpointBadge/InitiatingCloudEndpointBadge';
import {
  graphPersistedQueriesRouteConfig,
  graphProposalListRouteConfig,
  graphSettingsRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { HiddenBadge } from 'src/components/common/hiddenBadge/HiddenBadge';
import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { match } from 'src/components/common/typeahead/fuzzy';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { FavoriteButton } from 'src/components/favoriteButton/FavoriteButton';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useHandleFavoriteVariant } from 'src/hooks/useHandleFavoriteVariant';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { scrollToIfNotInView } from 'src/lib/scrollTo';

const calcRequestsPerMin = (requestsInLastDay: number) => {
  const totalMinutes = moment.duration(24, 'hours').asMinutes();
  return format.requests(requestsInLastDay / totalMinutes);
};

const MaybeUnavailable = ({
  value,
  unavailableTitle,
}: {
  value: string | null;
  unavailableTitle?: string;
}) => {
  const unavailable = !value || value === '-';
  return (
    <Tooltip label={unavailable ? unavailableTitle : undefined}>
      {unavailable ? (
        <IconUnavailable className="text-disabled" />
      ) : (
        <span>{value}</span>
      )}
    </Tooltip>
  );
};

enum SortableField {
  name = 'name',
  subgraphs = 'subgraphs',
  lastPublished = 'activeSchemaPublish.publishedAt',
  types = 'activeSchemaPublish.schema.typeCount',
  fields = 'activeSchemaPublish.schema.fieldCount',
  requestsPerMin = 'requestsInLastDay',
  setupProgress = 'setupProgress',
  entities = 'entities',
}

const SupergraphListGraphFirstLoadMetadataFragment = gql`
  fragment SupergraphListGraphFirstLoadMetadataFragment on GraphVariant {
    id
    name
    isFavoriteOfCurrentUser
    isProtected
    isContract
    isPublic
    hasManagedSubgraphs
    hasSupergraphSchema
    latestLaunch {
      id
      build {
        id
        result {
          ... on BuildFailure {
            errorMessages {
              message
            }
          }
        }
      }
    }
    activeSchemaPublish {
      id
      publishedAt
      schema {
        hash
        createdAt
        typeCount
        fieldCount
      }
    }
  }
`;

export const SupergraphsListItemGraphFragment = gql`
  fragment SupergraphsListItemGraphFragment on Service {
    id
    graphType
    title
    avatarUrl
    description
    hiddenFromUninvitedNonAdminAccountMembers
    roles {
      canQueryTokens
      canUpdateTitle
      canUpdateAvatar
      canQueryPersistedQueryLists
      canQueryProposals
      canQueryStats
      canQueryImplementingServices
    }
    variants {
      id
      ...SupergraphListGraphFirstLoadMetadataFragment
    }
  }
  ${SupergraphListGraphFirstLoadMetadataFragment}
`;

export const SupergraphListGraphDeferredMetadataFragment = gql`
  fragment SupergraphListGraphDeferredMetadataFragment on Service {
    id
    graphType
    variants {
      id
      requestsInLastDay
      usageLastReportedAt
      subgraphs {
        name
      }
      entities {
        ... on EntitiesResponse {
          entities {
            typename
          }
        }
      }
    }
  }
`;

const SupergraphsVariantsTableQuery = gql`
  query SupergraphsVariantsTableQuery($graphId: ID!) {
    graph: service(id: $graphId) {
      id
      ...SupergraphListGraphDeferredMetadataFragment
    }
  }
  ${SupergraphListGraphDeferredMetadataFragment}
`;

const SupergraphsVariantsTable = ({
  isInFavSection,
  inView,
  graph: { id: graphId, variants, graphType, roles },
}: {
  isInFavSection: boolean;
  inView: boolean;
  graph: GraphQLTypes.SupergraphsListItemGraphFragment;
}) => {
  const location = useLocation();
  const { handleFavorite } = useHandleFavoriteVariant();
  const { data, loading, error } = useQuery<
    GraphQLTypes.SupergraphsVariantsTableQuery,
    GraphQLTypes.SupergraphsVariantsTableQueryVariables
  >(SupergraphsVariantsTableQuery, {
    variables: { graphId },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    skip: !inView,
  });

  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [selectedField, setSelectedField] = useState<SortableField>(
    SortableField.name,
  );

  const handleSort = ({
    field,
    nextAscending,
  }: {
    field: SortableField;
    nextAscending: boolean;
  }) => {
    setSelectedField(field);
    setSortAscending(nextAscending);
  };

  const { initiatingSupergraphState } = useRouteParams(orgRoutes.graphs);

  const sortedVariants = useMemo(() => {
    const variantsMergedWithStats = variants.map((variant) => {
      const variantStats = data?.graph?.variants.find(
        (v) => v.id === variant.id,
      );
      return {
        ...variant,
        subgraphs: variantStats?.subgraphs,
        requestsInLastDay: variantStats?.usageLastReportedAt
          ? variantStats.requestsInLastDay || 0
          : undefined,
        entities:
          variantStats?.entities?.__typename === 'EntitiesResponse'
            ? variantStats.entities.entities.length
            : undefined,
      };
    });
    return orderBy(
      isInFavSection
        ? variantsMergedWithStats?.filter(
            ({ isFavoriteOfCurrentUser }) => isFavoriteOfCurrentUser,
          )
        : variantsMergedWithStats,
      selectedField,
      sortAscending ? 'asc' : 'desc',
    );
  }, [selectedField, isInFavSection, sortAscending, data, variants]);

  if (error) {
    return (
      <ErrorMessage>
        Hmm, something went wrong when trying to load this graph. Please try
        reloading your browser.
      </ErrorMessage>
    );
  }

  return (
    <TableContainer>
      <Table
        className="table-fixed"
        sortBy={selectedField}
        sortOrder={sortAscending ? 'asc' : 'desc'}
        onRequestSort={({ sortBy, sortOrder }) => {
          handleSort({ field: sortBy, nextAscending: sortOrder === 'asc' });
        }}
      >
        <Thead>
          <Tr>
            <Th className="w-64">
              <Group>
                Variant name
                <TableSortButton attribute={SortableField.name} />
              </Group>
            </Th>
            {/* Used to provide min-width for first column */}
            <Th />
            <Th className="w-44 whitespace-nowrap">
              <Group>
                Last published
                <TableSortButton attribute={SortableField.lastPublished} />
              </Group>
            </Th>
            <Th isNumeric className="w-[92px]">
              <Group className="justify-end">
                <TableSortButton attribute={SortableField.types} />
                Types
              </Group>
            </Th>
            <Th isNumeric className="w-24">
              <Group className="justify-end">
                <TableSortButton attribute={SortableField.fields} />
                Fields
              </Group>
            </Th>
            {roles?.canQueryStats && (
              <>
                {roles?.canQueryImplementingServices && (
                  <Th isNumeric className="w-[136px]">
                    <Group className="justify-end">
                      <TableSortButton attribute={SortableField.subgraphs} />
                      Subgraphs
                    </Group>
                  </Th>
                )}
                <Th isNumeric className="w-[108px]">
                  <Group className="justify-end">
                    <TableSortButton attribute={SortableField.entities} />
                    Entities
                  </Group>
                </Th>
                <Th isNumeric className="w-[156px]">
                  <Group className="justify-end">
                    <TableSortButton attribute={SortableField.requestsPerMin} />
                    Requests/min
                  </Group>
                </Th>
              </>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {sortedVariants.map((variant) => {
            const {
              id,
              name,
              activeSchemaPublish,
              entities,
              isProtected,
              isContract,
              isPublic,
              isFavoriteOfCurrentUser,
              latestLaunch,
              hasManagedSubgraphs,
              requestsInLastDay,
              subgraphs,
            } = variant;

            const buildErrors =
              latestLaunch?.build?.result?.__typename === 'BuildFailure'
                ? latestLaunch.build.result.errorMessages
                : [];

            return (
              <Tr key={id}>
                <Td colSpan={2}>
                  <Group gap={2}>
                    <FavoriteButton
                      className="flex-shrink-0 !px-0"
                      data-testid="supergraphs-list-favorite-button"
                      isFavorite={isFavoriteOfCurrentUser}
                      ariaLabel={`Click to ${
                        isFavoriteOfCurrentUser ? 'unfavorite' : 'favorite'
                      } ${name} variant`}
                      onClick={() => {
                        handleFavorite({
                          serviceId: graphId,
                          favorite: !isFavoriteOfCurrentUser,
                          variantName: name,
                          variantId: id,
                        });
                      }}
                    />
                    <Group gap={2} className="flex-wrap">
                      <Link
                        {...(initiatingSupergraphState &&
                        initiatingSupergraphState.graphId === graphId &&
                        initiatingSupergraphState.graphVariant === name
                          ? {
                              'data-analytics-category': '6M2S: Onboarding',
                              'data-analytics-label':
                                'Variant link click immediately after creation',
                            }
                          : {})}
                        data-testid={`supergraph-list-variant-link-${graphId}-${name}`}
                        to={variantHomepageRouteConfig.location({
                          graphId,
                          graphVariant: name,
                        })}
                        className="break-all"
                      >
                        {name}
                      </Link>
                      {initiatingSupergraphState && (
                        <IsCloudEndpointInitiatingContextProvider
                          graphRef={{ graphId, graphVariant: name }}
                        >
                          <InitiatingCloudEndpointBadge />
                        </IsCloudEndpointInitiatingContextProvider>
                      )}
                      {isProtected && (
                        <Tooltip label="Protected variants can only be modified by Graph Admins and Org Admins">
                          <IconLock className="icon-warning flex-shrink-0" />
                        </Tooltip>
                      )}
                      {isPublic && (
                        <Tooltip label="Public">
                          <IconPublic className="icon-info flex-shrink-0" />
                        </Tooltip>
                      )}
                      {isContract && <Badge>Contract</Badge>}
                      {graphType !== GraphQLTypes.GraphType.CLOUD_SUPERGRAPH &&
                        hasManagedSubgraphs && <Badge>Federated</Badge>}
                      {isMinLengthArray(1, buildErrors) && (
                        <ReactRouterLink
                          className="inline-flex"
                          to={setOverlayVisibleLocation({
                            location,
                            additionalSearchParameters: {
                              graphId,
                              variant: name,
                            },
                            overlayName: Config.modals.buildErrors,
                          })}
                        >
                          <Badge variant="error">Build Failed</Badge>
                        </ReactRouterLink>
                      )}
                    </Group>
                  </Group>
                </Td>
                <Td>
                  <div className="whitespace-nowrap text-base">
                    {activeSchemaPublish ? (
                      <RelativeTimestamp
                        value={activeSchemaPublish.publishedAt}
                        withTooltip={false}
                      />
                    ) : (
                      'never'
                    )}
                  </div>
                </Td>
                <Td isNumeric>
                  <MaybeUnavailable
                    unavailableTitle="No schema has been published"
                    value={
                      activeSchemaPublish
                        ? format.number(
                            activeSchemaPublish.schema.typeCount,
                            '0.[.]0a',
                          )
                        : null
                    }
                  />
                </Td>
                <Td isNumeric>
                  <MaybeUnavailable
                    unavailableTitle="No schema has been published"
                    value={
                      activeSchemaPublish
                        ? format.number(
                            activeSchemaPublish.schema.fieldCount,
                            '0.[.]0a',
                          )
                        : null
                    }
                  />
                </Td>

                {roles?.canQueryStats && (
                  <>
                    {roles?.canQueryImplementingServices && (
                      <Td isNumeric>
                        {loading ? (
                          <ShimmerLoading className="my-3.5 w-full" />
                        ) : roles?.canQueryImplementingServices && subgraphs ? (
                          <MaybeUnavailable
                            unavailableTitle="This variant has no subgraphs"
                            value={subgraphs.length.toLocaleString()}
                          />
                        ) : null}
                      </Td>
                    )}
                    <Td isNumeric>
                      {loading ? (
                        <ShimmerLoading className="my-3.5 w-full" />
                      ) : entities === undefined ? null : (
                        format.number(entities, '0.[.]0a')
                      )}
                    </Td>
                    <Td isNumeric>
                      {loading ? (
                        <ShimmerLoading className="my-3.5 w-full" />
                      ) : (
                        <MaybeUnavailable
                          unavailableTitle="No metrics have been sent"
                          value={
                            requestsInLastDay
                              ? calcRequestsPerMin(requestsInLastDay)
                              : null
                          }
                        />
                      )}
                    </Td>
                  </>
                )}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const SupergraphsListItem = ({
  searchText,
  isInFavSection = false,
  graph,
}: {
  searchText?: string;
  isInFavSection?: boolean;
  graph: GraphQLTypes.SupergraphsListItemGraphFragment;
}) => {
  const {
    id: graphId,
    title,
    avatarUrl,
    description,
    variants,
    hiddenFromUninvitedNonAdminAccountMembers,
    roles,
  } = graph;
  const [inViewRef, inView] = useInView();
  const [expanded, setExpanded] = useState(true);
  const [highlightFinished, setHighlightFinished] = useState(false);

  const { highlightSupergraphState } = useRouteParams(orgRoutes.graphs);
  const shouldHighlight =
    highlightSupergraphState?.graphId === graphId && !highlightFinished;

  const favs = variants.filter(
    ({ isFavoriteOfCurrentUser }) => isFavoriteOfCurrentUser,
  );

  const graphHasFederatedVariants = useMemo(
    () =>
      variants.some(
        (variant) => variant.hasManagedSubgraphs || variant.hasSupergraphSchema,
      ),
    [variants],
  );

  const canAccessProposals = useCapabilities().capabilities?.proposals;

  useEffect(() => {
    // remove highlight state after 5s after data has loaded
    // don't do timing based visual changes on chromatic
    if (highlightSupergraphState?.graphId === graphId) {
      scrollToIfNotInView(`#${graphId}`);
      const timeout = setTimeout(() => {
        setHighlightFinished(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [graphId, highlightSupergraphState?.graphId, shouldHighlight]);

  return (
    <Card
      id={graphId}
      className={classnames(
        'mb-4 transition-background duration-500',
        shouldHighlight && 'bg-highlight',
      )}
      ref={inViewRef}
    >
      <CardHeader>
        <Group gap={4}>
          {!isInFavSection &&
            (avatarUrl ? (
              <Avatar src={avatarUrl} variant="graph" />
            ) : (
              <MarbledAvatar
                animate={inView}
                identifier={graphId}
                className="flex-none"
              />
            ))}
          <Group orientation="vertical">
            <CardTitle className="flex flex-1 items-center">
              {searchText ? (
                <span>
                  {
                    match(searchText, title, {
                      elem: (
                        <span className="bg-searchhighlight text-inverted" />
                      ),
                    })?.rendered
                  }
                </span>
              ) : (
                title
              )}
              {hiddenFromUninvitedNonAdminAccountMembers ? (
                <HiddenBadge />
              ) : null}
            </CardTitle>
            {(isInFavSection || description) && (
              <CardDescription>
                {isInFavSection
                  ? `${favs.length} favorited ${format.pluralize(
                      favs.length,
                      'variant',
                    )}`
                  : description}
              </CardDescription>
            )}
          </Group>
        </Group>
        <ButtonGroup>
          {graphHasFederatedVariants &&
            canAccessProposals &&
            roles?.canQueryProposals && (
              <Tooltip placement="top" label="Go to schema change proposals">
                <IconButton
                  data-testid={`supergraph-list-proposals-button-${graphId}`}
                  aria-label="Go to schema change proposals"
                  variant="hidden"
                  size="sm"
                  icon={<IconProposals />}
                  as={ReactRouterLink}
                  to={graphProposalListRouteConfig.path({
                    graphId,
                    status: [
                      GraphQLTypes.ProposalStatus.OPEN,
                      GraphQLTypes.ProposalStatus.DRAFT,
                      GraphQLTypes.ProposalStatus.APPROVED,
                    ],
                  })}
                />
              </Tooltip>
            )}

          {variants[0]?.name && roles?.canQueryPersistedQueryLists && (
            <Tooltip placement="top" label="Go to persisted query lists">
              <IconButton
                data-testid={`supergraph-list-knownops-button-${graphId}`}
                aria-label="Go to persisted query lists"
                variant="hidden"
                size="sm"
                icon={<IconOperations />}
                as={ReactRouterLink}
                to={graphPersistedQueriesRouteConfig.path({ graphId })}
              />
            </Tooltip>
          )}

          {variants[0]?.name &&
            roles?.canQueryTokens &&
            roles?.canUpdateTitle &&
            roles?.canUpdateAvatar && (
              <Tooltip placement="top" label="Go to graph settings">
                <IconButton
                  data-testid={`supergraph-list-setting-button-${graphId}`}
                  aria-label="Go to graph settings"
                  variant="hidden"
                  size="sm"
                  icon={<IconSettings />}
                  as={ReactRouterLink}
                  to={graphSettingsRouteConfig.path({
                    graphId,
                    graphVariant: variants[0].name,
                  })}
                />
              </Tooltip>
            )}
          <Tooltip
            placement="top"
            label={expanded ? 'Collapse variants' : 'Expand variants'}
          >
            <IconButton
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse variants' : 'Expand variants'}
              variant="hidden"
              size="sm"
              icon={expanded ? <IconChevronDown /> : <IconChevronRight />}
            />
          </Tooltip>
        </ButtonGroup>
      </CardHeader>
      {expanded && (
        <CardBody>
          <SupergraphsVariantsTable
            isInFavSection={isInFavSection}
            inView={inView}
            graph={graph}
          />
        </CardBody>
      )}
    </Card>
  );
};
